import { IFakturowniaInvoiceListRow, IInvoiceWithNet } from './types';

/**
 * Net per-row outstanding, then drop correction rows themselves. Phase 7:
 * backend went with **net semantics** (aggregates already sum original +
 * negative correction), but individual list rows still carry their own
 * outstanding. For PendingSection / OverdueList we want to chase clients
 * by the post-correction debt, not by either side in isolation —
 * `netOutstanding = parent.outstanding + Σ corrections.outstanding`. The
 * correction row itself is hidden because it's surfaced under the parent
 * on the detail page; showing it in pending lists just double-counts.
 *
 * This replaces the Phase 6 `stripCorrected` helper, which incorrectly
 * hid the original whenever ANY correction existed — that swallowed
 * partial-refund cases (e.g. owed 100, correction −30 → real debt 70 →
 * old code hid both, showing 0 instead of 70).
 *
 * Phase 12: also drop quotations (estimate / client_order / proforma).
 * The chain math is `Σ(advance) + final == estimate`, so including any
 * one of these three in a client-side sum double-counts the deal.
 * `fakturowniaInvoiceAggregates` excludes them by default on the server;
 * extending the helper keeps the list-derived sums consistent so the
 * Summary KPIs match the dashboard aggregates.
 */
export const withNetOutstanding = (
  rows: IFakturowniaInvoiceListRow[],
): IInvoiceWithNet[] =>
  rows
    .filter((r) => !r.isCorrection && !r.isQuotation)
    .map((r) => {
      const childrenSum = (r.corrections || []).reduce(
        (s, c) => s + (c?.outstanding || 0),
        0,
      );
      return {
        ...r,
        netOutstanding: (r.outstanding || 0) + childrenSum,
      };
    });
